import type { Activity, ActivityEnvelope } from "./types.js";

/** The one activity type this client knows how to read producer data for. */
const BIS_SESSION_TYPE = "no.bankid.bis.internal.session.v1";

const ACTION_BY_PERMISSION_TYPE: Record<string, string> = {
  "authentication.v1": "login",
  "payment.v1": "payment",
  "cardPayment.v1": "card_payment",
  "webAuthnKeyRegistration.v1": "key_registration",
  "text.v1": "text_approval",
  "verifiedConfirmation.v1": "confirmation",
  "legacyPaymentText.v1": "legacy_payment",
  "approveCard.v1": "approve_card",
  "approveAccount.v1": "approve_account",
  "customerServiceCall.v1": "customer_service_call",
  "customerServiceChat.v1": "customer_service_chat",
};

const STATUS_BY_SESSION_STATUS: Record<string, string> = {
  succeeded: "success",
  failed: "failed",
  abandoned: "abandoned",
};

function readString(
  data: ActivityEnvelope["data"],
  field: string,
): string | null {
  const value = data[field];
  return typeof value === "string" ? value : null;
}

/**
 * Transform a published envelope into the render model. Dispatch is a single
 * exact type comparison: the known type is rendered from its producer `data`,
 * and anything else is rendered from the server-authored `display` text only,
 * so an unrecognised type can never put a raw type or producer value on screen.
 */
export function toRenderableActivity(envelope: ActivityEnvelope): Activity {
  return envelope.type === BIS_SESSION_TYPE
    ? fromBisSessionData(envelope)
    : fromDisplay(envelope);
}

function fromBisSessionData(envelope: ActivityEnvelope): Activity {
  const data = envelope.data;
  const permissionType = readString(data, "permissionType") ?? "";
  const sessionStatus = readString(data, "sessionStatus") ?? "";
  const flowType = readString(data, "flowType");
  const amr = data.amr;

  const tags = flowType ? [`flow:${flowType}`] : [];
  if (Array.isArray(amr)) {
    for (const method of amr) {
      if (typeof method === "string") tags.push(`amr:${method}`);
    }
  }

  return {
    id: envelope.id,
    time: envelope.time,
    action: ACTION_BY_PERMISSION_TYPE[permissionType] ?? permissionType,
    actor: { name: readString(data, "merchantName"), type: "service" },
    location: { type: "web" },
    status: STATUS_BY_SESSION_STATUS[sessionStatus] ?? sessionStatus,
    tags,
    ...(envelope.display.imageUrl
      ? { imageUrl: envelope.display.imageUrl }
      : {}),
  };
}

function fromDisplay(envelope: ActivityEnvelope): Activity {
  const display = envelope.display;
  return {
    id: envelope.id,
    time: envelope.time,
    action: display.context,
    actor: { name: display.relyingParty, type: "service" },
    status: "",
    tags: [],
    ...(display.imageUrl ? { imageUrl: display.imageUrl } : {}),
  };
}

export interface ActivityGroup {
  key: string;
  label: string;
  activities: Activity[];
}

/**
 * Group activities by calendar day. Each group's label is the day's date
 * formatted for the current locale (e.g. "13. mars", "March 13"), matching the
 * activity-overview design. Groups are ordered newest day first.
 */
export function groupActivitiesByDay(activities: Activity[]): ActivityGroup[] {
  const groups = new Map<string, Activity[]>();
  const dates = new Map<string, Date>();

  for (const activity of activities) {
    const date = new Date(activity.time);
    if (Number.isNaN(date.getTime())) continue;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const existing = groups.get(key);
    if (existing) {
      existing.push(activity);
    } else {
      groups.set(key, [activity]);
      dates.set(key, date);
    }
  }

  const lang = document.documentElement.lang || "en";
  const now = new Date();
  const result: ActivityGroup[] = [];

  for (const [key, groupActivities] of groups) {
    const date = dates.get(key) as Date;
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
    };
    if (date.getFullYear() !== now.getFullYear()) {
      options.year = "numeric";
    }
    const label = date.toLocaleDateString(lang, options);
    result.push({ key, label, activities: groupActivities });
  }

  // Sort groups by key descending (newest day first)
  result.sort((a, b) => b.key.localeCompare(a.key));

  return result;
}

export function filterActivities(
  activities: Activity[],
  query: string,
): Activity[] {
  if (!query) return activities;
  const lowerQuery = query.toLowerCase();
  return activities.filter((activity) => {
    const merchant = (activity.actor.name ?? "").toLowerCase();
    return merchant.includes(lowerQuery);
  });
}

export function getActivityInitial(activity: Activity): string {
  const name = activity.actor?.name ?? "";
  return name.charAt(0).toUpperCase() || "?";
}
